import {MastersState} from '@target-web/leadads-form-view';
import {useState, useEffect, useMemo, useCallback} from 'react';
import {fetchMasterSeances, fetchMasters} from 'utils/requests';
import {useSearchParams} from 'utils/searchParamsContext';

type useDatesProps = {
    selectedStaffId: number | null;
    needDateTime?: boolean; // на прошлых шагах не выбрали дату, и сетим ее тут
    active?: boolean; // при активном состоянии перезапрашиваем данные
    service_ids?: number[]; // Массив ID услуг. Если нужны даты, когда можно забронировать указанные услуги
    datetime?: string; // дата (в формате iso8601). Если нужны сотрудники, у которых есть сеансы на указанную услугу в указанное время
    resetStaffId: () => void;
    resetDateTime: () => void;
};

export const useMasters = ({
    needDateTime = true, // когда false - значит дату выбрали на прошлом шаге, или выберут на следующем (но не на шаге мастера)
    selectedStaffId,
    datetime,
    resetStaffId,
    resetDateTime,
    ...props
}: useDatesProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [masters, setMasters] = useState<MastersState['masters']>([]);
    const {formDataHash} = useSearchParams();

    const searchParams = useMemo(() => {
        const params = new URLSearchParams({lead_form_data: formDataHash});

        if (props.service_ids?.length) {
            props.service_ids.forEach((id) => params.append('service_ids[]', String(id)));
        }

        if (datetime && !needDateTime) {
            params.append('datetime', datetime.slice(0, 19));
        }

        return params.toString();
    }, [props.service_ids, datetime, formDataHash, needDateTime]);

    const timesSearchParams = useMemo(() => {
        return new URLSearchParams({lead_form_data: formDataHash}).toString();
    }, [formDataHash]);

    const getMasters = useCallback(async () => {
        setIsLoading(true);
        const masters = await fetchMasters(searchParams);
        masters.sort((a, b) => a.weight - b.weight).sort((a, b) => (a.bookable ? -1 : 1));

        let masterDeleted = false;
        // если выбран мастер, но его нет в обновленном списке - анселектнем его
        if (selectedStaffId && !masters.map(({id}) => id).includes(selectedStaffId)) {
            resetStaffId();
            masterDeleted = true;
            if (needDateTime && datetime) {
                // если дата есть и ее засетили на шаге мастера - скинем
                resetDateTime();
            }
        }

        if (needDateTime) {
            Promise.all(masters.map((master) => fetchMasterSeances(master.id, timesSearchParams))).then((times) => {
                // ВАЖНО - время мастеров мы запрашиваем в порядке мастеров по индексу
                const mastersWithSeances = masters.map((master, i) => {
                    // мастера еще не анселектнули, выбрана дата
                    if (!masterDeleted && datetime && master.id === selectedStaffId) {
                        const masterTimes = times[i].data.data;
                        // если у мастера уже недоступен выбранный сеанс - анселектнем время
                        if (!masterTimes.seances?.map((s) => s.datetime).includes(datetime)) {
                            resetDateTime();
                        }
                    }
                    return {
                        ...master,
                        ...times[i].data.data,
                    };
                });

                setMasters(mastersWithSeances);
                setIsLoading(false);
            });
        } else {
            setMasters(masters);
            setIsLoading(false);
        }
    }, [searchParams, selectedStaffId, needDateTime, resetStaffId, datetime, resetDateTime, timesSearchParams]);

    useEffect(() => {
        if (props.active) {
            getMasters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.active]);

    const resetMasters = useCallback(() => {
        setMasters([]);
    }, []);

    return {
        isLoading,
        masters,
        resetMasters,
    };
};
